import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { logActivity } from '@/lib/activityLog';

const EMAIL_DOMAIN = 'tapevault.com';

export interface Profile {
  id: string;
  username: string;
  email: string;
  role: string;
  is_active: boolean;
  is_owner?: boolean;
  created_at?: string;
}

interface SignInResult {
  error?: string;
}

interface AuthContextValue {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (username: string, password: string, captchaToken?: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const SESSION_INIT_TIMEOUT_MS = 8_000;

async function getSessionWithTimeout() {
  return Promise.race([
    supabase.auth.getSession(),
    new Promise<never>((_, reject) => {
      window.setTimeout(
        () => reject(new Error('Session initialization timed out')),
        SESSION_INIT_TIMEOUT_MS,
      );
    }),
  ]);
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      setProfile(error || !data ? null : (data as Profile));
    } catch {
      setProfile(null);
    }
  }, []);

  useEffect(() => {
    let active = true;
    getSessionWithTimeout()
      .then(({ data }) => {
        if (!active) return;
        setUser(data.session?.user ?? null);
        if (data.session?.user) loadProfile(data.session.user.id);
      })
      .catch(() => {
        // A broken or slow auth endpoint must not leave the entire SPA on a
        // permanent loading screen. The protected layout will redirect to
        // the login page when no usable session is available.
        if (active) {
          setUser(null);
          setProfile(null);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setProfile(null);
      }
    });

    // When the browser restores a page from the back/forward cache (bfcache),
    // React state (including a previously-authenticated `user`) is restored
    // verbatim while the component does NOT re-mount, so getSession() above
    // never re-runs. Re-check the session here to close the gap where a user
    // who signed out could press "Back" and still see the protected UI.
    const handlePageshow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      getSessionWithTimeout()
        .then(({ data }) => {
          setUser(data.session?.user ?? null);
          if (data.session?.user) {
            loadProfile(data.session.user.id);
          } else {
            setProfile(null);
          }
        })
        .catch(() => {
          setUser(null);
          setProfile(null);
        });
    };
    window.addEventListener('pageshow', handlePageshow);

    return () => {
      active = false;
      sub.subscription.unsubscribe();
      window.removeEventListener('pageshow', handlePageshow);
    };
  }, [loadProfile]);

  const signIn = useCallback(
    async (username: string, password: string, captchaToken?: string): Promise<SignInResult> => {
      const trimmed = username.trim().toLowerCase();
      const email = trimmed.includes('@') ? trimmed : `${trimmed}@${EMAIL_DOMAIN}`;
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      });
      if (error) return { error: error.message };
      if (data.user) {
        await loadProfile(data.user.id);
        await logActivity('login', `Signed in as ${trimmed}`);
      }
      return {};
    },
    [loadProfile],
  );

  const signOut = useCallback(async () => {
    await logActivity('logout');
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) await loadProfile(user.id);
  }, [user, loadProfile]);

  return (
    <AuthContext.Provider
      value={{ user, profile, loading, signIn, signOut, refreshProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
